import React, { useEffect, useState, useRef } from "react";
import httpClient from "../../_util/api";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Autocomplete,
  Tabs,
  Tab,
  Button,
} from "@mui/material";
import { Download } from "@mui/icons-material";
import ButtonMain from "../../components/Button";
import { hideLoader, showLoader } from "../../Store/mainSlice";
import CustomizedSnackbars from "../../shared/Snackbar/SnackBar";
import { useDispatch } from "react-redux";
import { downloadCSV } from "../../components/Reports/downloadCSV";

const TAB_APPROVED = "approved";
const TAB_REJECTED = "rejected";

const Report = () => {
  const dispatch = useDispatch();
  const snackbar_Ref = useRef();
  const [universities, setUniversities] = useState([]);
  const [selectedUniversity, setSelectedUniversity] = useState("");
  const [activeTab, setActiveTab] = useState(TAB_APPROVED);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalApplications, setTotalApplications] = useState(0);

  const getAllPakistaniUni = async () => {
    const res = await httpClient.get("students/getAllPakUniversities").catch(() => {});
    if (res?.status === 200) {
      setUniversities(["Other", ...res.data.map((item) => item.name)]);
    }
  };

  const fetchApplications = async (pageNumber = page, limit = rowsPerPage, university = selectedUniversity) => {
    try {
      const res = await httpClient.get(
        `/report/reviewer-approver/${activeTab}?page=${pageNumber + 1}&limit=${limit}&search=${university ?? ""}`
      );
      if (res.status === 200) {
        setData(res.data.applications);
        setTotalApplications(res.data.pagination?.totalApplications ?? 0);
      } else {
        setData([]);
        setTotalApplications(0);
      }
    } catch (error) {
      setData([]);
      setTotalApplications(0);
    }
  };

  useEffect(() => {
    getAllPakistaniUni();
  }, []);

  useEffect(() => {
    setPage(0);
    fetchApplications(0, rowsPerPage, selectedUniversity);
  }, [activeTab, selectedUniversity]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchApplications(newPage, rowsPerPage, selectedUniversity);
  };

  const handleChangeRowsPerPage = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setRowsPerPage(newLimit);
    setPage(0);
    fetchApplications(0, newLimit, selectedUniversity);
  };

  const handleUniversityChange = (event, newValue) => {
    setSelectedUniversity(newValue ?? "");
    setPage(0);
  };

  const handleDownload = async () => {
    dispatch(showLoader());
    try {
      const res = await httpClient.get(
        `/report/reviewer-approver/${activeTab}/download?search=${selectedUniversity ?? ""}`
      );
      dispatch(hideLoader());
      if (res.status === 200 && res.data.applications?.length) {
        const reportDate = new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD
        const rows = res.data.applications.map((app, index) => {
          const notesText = Array.isArray(app.notes) && app.notes.length
            ? app.notes.map((n) => (n.remarks || "").trim()).filter(Boolean).join(", ") || "-"
            : "-";
          return {
            "S.No.": index + 1,
            "Application Number": app.applicationNumber,
            "Applicant Name": (app.applicantName || "").trim() || "-",
            "Email": app.email || "-",
            "CNIC": app.CNICNo || "-",
            "Institution": app.otherInstitutionName || app.institutionName || "-",
            "Faculty": app.otherFaculty || app.faculty || "-",
            "Semester": app.grade ?? "-",
            "Result": app.classOfLastInstitution ?? "-",
            "Enrollment #": app.enrollmentNo || "-",
            "Tuition Fee": app.tuttionFee ?? "-",
            "Approved Fee": app.approvedFee ?? "-",
            "Application Date": app.applicationSubmitDate || "-",
            "Status": activeTab === TAB_REJECTED ? "Rejected" : (app.status || "-"),
            "Notes": notesText,
            "Report Date": reportDate,
          };
        });
        const dateForFile = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
        downloadCSV(rows, `${activeTab}-applications-report-${dateForFile}`);
        snackbar_Ref.current.showMessage("success", "Report downloaded successfully.", "", "i-chk-circle");
      } else {
        snackbar_Ref.current.showMessage("warning", "No applications to download.", "", "i-info-circle");
      }
    } catch (error) {
      dispatch(hideLoader());
      snackbar_Ref.current.showMessage(
        "error",
        error?.response?.data?.message || "Download failed.",
        "",
        "i-error-circle"
      );
    }
  };

  return (
    <Paper sx={{ width: "100%", overflow: "hidden", padding: 2 }}>
      <CustomizedSnackbars ref={snackbar_Ref} />
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2, mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => { setActiveTab(v); setPage(0); }}
          sx={{ minHeight: 40 }}
        >
          <Tab label="Approved" value={TAB_APPROVED} />
          <Tab label="Rejected" value={TAB_REJECTED} />
        </Tabs>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Autocomplete
            options={universities}
            value={selectedUniversity}
            onChange={handleUniversityChange}
            sx={{ width: 300 }}
            renderInput={(params) => (
              <TextField {...params} label="University / Institution" variant="outlined" size="small" />
            )}
          />
          <ButtonMain startIcon={<Download />} onClick={handleDownload}>
            Download
          </ButtonMain>
        </Box>
      </Box>
      <TableContainer>
        <Table>
          <TableHead sx={{ backgroundColor: "#4caf50" }}>
            <TableRow>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>Application No</TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>Enrollment #</TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>Institution</TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>Semester</TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>Tuition Fee</TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>Approved Fee</TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>Submission Date</TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No applications found
                </TableCell>
              </TableRow>
            ) : (
              data.map((item) => (
                <TableRow key={item._id || item.applicationNumber}>
                  <TableCell>{item.applicationNumber}</TableCell>
                  <TableCell>{item.enrollmentNo || "-"}</TableCell>
                  <TableCell>{item.otherInstitutionName || item.institutionName}</TableCell>
                  <TableCell>{item.grade}</TableCell>
                  <TableCell>{item.tuttionFee}</TableCell>
                  <TableCell>{item.approvedFee}</TableCell>
                  <TableCell>{item.applicationSubmitDate}</TableCell>
                  <TableCell style={{ textTransform: "capitalize" }}>{activeTab === TAB_REJECTED ? "Rejected" : (item.status || "-")}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={totalApplications}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
      />
    </Paper>
  );
};

export default Report;
